import { useTranslation } from 'react-i18next';

import { CircleIcon } from '@/components/icons';
import { Badge } from '@/components/ui/daisy';
import { Colors, Sizes, Variants } from '@/components/ui/types';
import {
  VersioningStatusEnum,
  VersioningStatusUIEnum,
} from '@/enums/VersioningStatus';
import { convertVersioningStatusToUIEnum } from '@/utils/versioning';
import {
  ArrowPathIcon,
  CheckIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type ContPedStatusProps = {
  status: VersioningStatusEnum;
  size?: Sizes;
  version: number;
};

export default function ContPedStatus({
  version,
  status,
  size,
}: ContPedStatusProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courses.status',
  });

  const uiStatus = convertVersioningStatusToUIEnum(status, version);

  const mapStatusToColor: Record<VersioningStatusUIEnum, Colors> = {
    [VersioningStatusUIEnum.VERSIONING]: 'warning',
    [VersioningStatusUIEnum.PUBLISHED]: 'success',
    [VersioningStatusUIEnum.EDITING]: 'warning',
    [VersioningStatusUIEnum.BETA]: 'info',
    [VersioningStatusUIEnum.DRAFT]: 'warning',
    [VersioningStatusUIEnum.DISABLED]: 'default',
  };

  const iconClassname = 'w-3.5 h-3.5';
  const mapStatusToIcon: Record<VersioningStatusUIEnum, React.ReactNode> = {
    [VersioningStatusUIEnum.VERSIONING]: (
      <ArrowPathIcon className={iconClassname} />
    ),
    [VersioningStatusUIEnum.PUBLISHED]: <CheckIcon className={iconClassname} />,
    [VersioningStatusUIEnum.EDITING]: (
      <PencilSquareIcon className={iconClassname} />
    ),
    [VersioningStatusUIEnum.BETA]: <CircleIcon className={iconClassname} />,
    [VersioningStatusUIEnum.DRAFT]: <ArrowPathIcon className={iconClassname} />,
    [VersioningStatusUIEnum.DISABLED]: <XMarkIcon className={iconClassname} />,
  };

  const mapStatusToText: Record<VersioningStatusUIEnum, string> = {
    [VersioningStatusUIEnum.VERSIONING]: t('VERSIONING'),
    [VersioningStatusUIEnum.PUBLISHED]: t('PUBLISHED'),
    [VersioningStatusUIEnum.EDITING]: t('EDITING'),
    [VersioningStatusUIEnum.BETA]: t('BETA'),
    [VersioningStatusUIEnum.DISABLED]: t('DISABLED'),
    [VersioningStatusUIEnum.DRAFT]: t('NEW'),
  };

  const mapStatusToVariant: Record<VersioningStatusUIEnum, Variants> = {
    [VersioningStatusUIEnum.VERSIONING]: 'dash',
    [VersioningStatusUIEnum.PUBLISHED]: 'soft',
    [VersioningStatusUIEnum.EDITING]: 'dash',
    [VersioningStatusUIEnum.BETA]: 'default',
    [VersioningStatusUIEnum.DRAFT]: 'dash',
    [VersioningStatusUIEnum.DISABLED]: 'default',
  };

  return (
    <Badge
      color={mapStatusToColor[uiStatus]}
      variant={mapStatusToVariant[uiStatus]}
      size={size}
    >
      {mapStatusToIcon[uiStatus]}
      {mapStatusToText[uiStatus]}
    </Badge>
  );
}
